import { formatHourMark, useTimeFormat } from '@/shared/lib/time-format';
import { locationTileBg, locationEdgeColor } from '@/scheduling/lib/location-colors';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import ghostMotion from './day-list-ghost-motion.module.css';
import {
  Ban,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  Check,
  LogIn,
  MoreHorizontal,
  Pencil,
  Repeat,
  Undo2,
  UserCheck,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { GutterClock } from '@/components/blocks/gutter-clock';
import { SkeletonBar } from '@/components/blocks/calendar-skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';
import { BLOCK_STRIPES } from '@/components/cards/appointment-tile';
import { StatusBadge } from '@/components/atoms';
import { PatientAvatar, ScripsIcon, type ScripsIconName } from '@/shared/ui/components';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import type { SchedulingGhostRow } from '@/scheduling/ai/ghost-rows';
import {
  getRowActionsFor,
  shouldShowBillingTabMenuItem,
  shouldShowViewRecurringSeriesMenuItem,
  shouldShowPvsMenuItem,
  statusCodeForAction,
  type AppointmentAction,
  type AppointmentActionId,
} from '@/scheduling/lib/appointment-actions';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import { usePermission } from '@/auth/permission-gate';
import {
  arrivedGuardStatus,
  arrivedGuardMessage,
  ARRIVED_GUARD_TITLE,
  ARRIVED_GUARD_PROCEED,
  type ArrivedGuardKind,
} from '@/scheduling/lib/arrived-guard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * DayListCalendar — Block
 *
 * Day-view list/table layout matching Flutter PM's
 * `calendar_by_day_list_widget.dart`. All icons render through the
 * Scrips icon font (same TTFs Flutter ships) so the action row is
 * pixel-identical to dev.
 */

interface DayListCalendarProps {
  rows: DayListRow[];
  /** Bookings (or the practitioners they hang on) still loading: grey card rows in the real layout, no empty state. */
  isLoading?: boolean;
  hours: number[];
  /** 0–23. The hour that gets the filled blue marker. */
  currentHour?: number;
  /** ISO date string (YYYY-MM-DD) currently being shown — drives the
   *  isToday gate that Flutter applies before rendering most actions. */
  selectedDateISO?: string;
  onAppointmentClick?: (id: string) => void;
  /** Fired when an action is non-status (cancel / reschedule / edit /
   *  follow-up / overflow). Status-changing actions are handled internally
   *  by the row via useUpdateAppointmentStatus — caller doesn't see them.
   *  Receives the full row so the consumer can build a dialog target
   *  without re-querying. */
  onAppointmentAction?: (row: DayListRow, action: AppointmentActionId) => void;
  /**
   * Scheduling-AI ghost draft rows (VITE_SCHEDULING_AI, fail-closed —
   * the frame passes an empty array when the flag is off or no draft is
   * active). VIEW-ONLY: dashed border, "Draft" tag, amber variant when
   * the backend rejected the day. No click handlers — the create rail's
   * own flow is the only thing that books; ghosts clear on rail close.
   */
  ghostRows?: SchedulingGhostRow[];
}

// 7 data columns + 1 trailing actions column. The columns and the 200 px actions column live in showcase-bridge
// (`.cal-day-cols`, "calendar day list, columns"). Every column stays at every width: a narrow list scrolls sideways.
// Every cell carries `data-col`, so the header and the rows line up cell for cell.
const DATA_GRID = 'cal-day-cols';

const GUTTER = 'w-16 flex-shrink-0';

// A row's icon action shares the one table icon-button style (RowActions).

const LOCATION_ICONS: Record<string, ScripsIconName> = {
  office: 'office',
  home: 'home',
  virtual: 'virtual',
  block: 'block',
};

export function DayListCalendar({
  rows,
  hours,
  currentHour,
  selectedDateISO,
  onAppointmentClick,
  onAppointmentAction,
  ghostRows = [],
  isLoading = false,
}: DayListCalendarProps) {
  // Hour bucketing parses the WALL-CLOCK hour straight off the ISO string
  // (#836 review MEDIUM 4): rows and ghosts both carry profile-timezone
  // wall-clock ISOs, and `new Date(...).getHours()` re-interprets them in the
  // MACHINE timezone — wrong hour for anyone whose OS zone differs from their
  // profile zone (travelling staff, mis-set workstation).
  const wallClockHour = (iso: string) => Number.parseInt(iso.slice(11, 13), 10) || 0;
  const byHour = new Map<number, DayListRow[]>();
  for (const r of rows) {
    const h = wallClockHour(r.startISO);
    const list = byHour.get(h);
    if (list) list.push(r);
    else byHour.set(h, [r]);
  }

  // Ghost drafts group by hour the same way real rows do; they render
  // AFTER the hour's real rows (or instead of the empty-hour divider).
  const ghostsByHour = new Map<number, SchedulingGhostRow[]>();
  for (const g of ghostRows) {
    const h = wallClockHour(g.startISO);
    const list = ghostsByHour.get(h);
    if (list) list.push(g);
    else ghostsByHour.set(h, [g]);
  }

  // Auto-scroll the body so the current hour is in view. Mirrors Flutter
  // `_scrollToIndex` (`calendar_by_day_list_widget.dart:387-404`).
  //
  // PROD-1774 — this used to bail out on `rows.length === 0`, so an EMPTY day
  // stayed pinned at 12a while Flutter scrolled to the current hour. The
  // comment defending that gate claimed Flutter gated equivalently because
  // `_scrollToIndex` "only fires after ChangeDateState, which carries loaded
  // data". Reading the Dart settled it: `ChangeDateState` carries only
  // `(date, filter)` (`scheduling_state.dart:318-323`) and is emitted
  // synchronously from `ChangeDateEvent` with no fetch in between
  // (`scheduling_bloc.dart:629-631`). Flutter's only list-shaped gate is
  // `leadingItems.length > 0` — the HOUR GUTTER, built from practice working
  // hours, never from appointments. That is `hours.length` here, which we
  // already check. `appointmentList` is never read by `_scrollToIndex`.
  //
  // The gate's second justification — that empty and appointment rows have
  // different heights — has a true premise and a false conclusion. Flutter's
  // heights differ too (empty hour `70`, appointment hour `count * 40`,
  // `calendar_by_day_list_widget.dart:548-601`) and Flutter scrolls anyway:
  // its `jumpTo(index:)` is index-based, so heights are irrelevant to it.
  // `scrollIntoView` is element-based but resolves against LIVE layout, so it
  // lands correctly for whatever layout is mounted at call time.
  //
  // The real risk the old comment was groping at is a post-data layout SHIFT,
  // and the old code made it worse: `didScrollRef` latched on first success,
  // so the re-run triggered by the `rows.length` dependency was swallowed and
  // the offset was never corrected. So instead of a boolean latch we remember
  // WHICH (date, hour, rowCount) we last scrolled for. Empty → loaded changes
  // the row count, which re-issues the scroll against the settled layout.
  // Same date + same hour + same data = no repeat, so we still never fight the
  // user's manual scrolling.
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrolledForRef = useRef<string | null>(null);
  // Experiment (Antonin, 13 Oct): the day is an agenda table, not a column of page-wide cards. `?day=cards` brings
  // back the card list for comparison.
  const [agenda] = useState(() => typeof window === 'undefined' || new URLSearchParams(window.location.search).get('day') !== 'cards');

  useEffect(() => {
    if (currentHour === undefined) return;
    // Flutter's `leadingItems.length > 0` gate — no gutter, nothing to scroll
    // to. This one IS parity; the removed `rows.length` check was not.
    if (hours.length === 0) return;

    const key = `${selectedDateISO}|${currentHour}|${rows.length}`;
    if (scrolledForRef.current === key) return;

    // One frame for layout to settle after the render commits.
    const id = window.setTimeout(() => {
      const el = bodyRef.current?.querySelector<HTMLElement>(
        `[data-hour="${currentHour}"]`,
      );
      if (!el) return;
      // scrollIntoView handles the case where the actual scroll container
      // is an ancestor outside our DayListCalendar root (FrameScheduling
      // wraps us in another `overflow-auto` div). The browser walks the
      // scroll-container chain and scrolls whichever one needs to move.
      // `scroll-margin-top` (set inline below) gives the row a 24 px
      // breathing gap from the top edge.
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
      scrolledForRef.current = key;
    }, 50);

    return () => window.clearTimeout(id);
  }, [currentHour, hours.length, rows.length, selectedDateISO]);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[var(--color-surface-card)]">
     {/* One scroller for the header and the rows (Antonin, 15 Oct: "solidly fixed"): the header pins to its top, and in
         a narrow window both scroll sideways together (showcase-bridge `[data-cal-*]`). */}
     <div data-cal-scroll className="scrollbar-hide">
     <div data-cal-sheet>
      {/* Sticky column header — gutter shows the live clock + GMT offset
          like Flutter; right side hosts the column titles. */}
      <div data-cal-head className="flex h-10 items-center border-b border-border-default">
        <div className={cn(GUTTER, 'flex flex-col items-center justify-center py-1')}>
          <GutterClock />
        </div>
        <div className={cn(DATA_GRID, 'flex-1 px-3 py-2 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary')}>
          <div className="min-w-0 truncate" data-col="time">Time (room)</div>
          <div className="min-w-0 truncate" data-col="patient">Patient</div>
          <div className="min-w-0 truncate" data-col="dob">DOB</div>
          <div className="min-w-0 truncate" data-col="doctor">Doctor</div>
          <div className="min-w-0 truncate" data-col="profile">Profile (RFV)</div>
          <div className="min-w-0 truncate" data-col="status">Status</div>
          <div className="min-w-0 truncate" data-col="note">Note</div>
          <div data-col="actions" /> {/* actions spacer (the actions column) */}
        </div>
      </div>

      {/* Body */}
      {agenda ? (
        <AgendaBody
          bodyRef={bodyRef}
          hours={hours}
          rows={rows}
          byHour={byHour}
          ghostsByHour={ghostsByHour}
          currentHour={currentHour}
          isLoading={isLoading}
          selectedDateISO={selectedDateISO}
          onAppointmentClick={onAppointmentClick}
          onAppointmentAction={onAppointmentAction}
        />
      ) : (
      <div ref={bodyRef} className="px-3 py-1">
        {hours.map((hour, hourIdx) => {
          const apts = byHour.get(hour) ?? [];
          const ghosts = ghostsByHour.get(hour) ?? [];
          const isCurrent = hour === currentHour;
          return (
            <div
              key={hour}
              data-hour={hour}
              // Reserve 64 px (the 40 px pinned header + 24) so scrollIntoView({ block: "start" }) lands
              // the current-hour row a hair below the sticky header
              // instead of flush against it.
              style={{ scrollMarginTop: 64 }}
            >
              {isLoading && rows.length === 0 ? (
                // Loading: every third hour holds a grey card the size of a real one, the rest are empty hours, so
                // the list keeps its rhythm and barely moves when the bookings land.
                hourIdx % 3 === 1 ? <SkeletonCardRow hour={hour} isCurrent={isCurrent} /> : <EmptyHourRow hour={hour} isCurrent={isCurrent} />
              ) : apts.length === 0 && ghosts.length === 0 ? (
                <EmptyHourRow hour={hour} isCurrent={isCurrent} />
              ) : (
                <>
                  {apts.map((apt, idx) => (
                    <AppointmentCardRow
                      key={apt.id}
                      row={apt}
                      hour={hour}
                      isFirstInHour={idx === 0}
                      isCurrentHour={isCurrent}
                      selectedDateISO={selectedDateISO}
                      onClick={onAppointmentClick}
                      onAction={onAppointmentAction}
                    />
                  ))}
                  {ghosts.map((ghost, idx) => (
                    <GhostDraftRow
                      key={ghost.id}
                      ghost={ghost}
                      hour={hour}
                      isFirstInHour={apts.length === 0 && idx === 0}
                      isCurrentHour={isCurrent}
                      /* Cascade index — a recurring series or a
                         back-to-back chain sketches itself onto the day
                         in order, on the same stagger as the rail's
                         cards, so the two halves read as ONE event. */
                      motionIndex={idx}
                    />
                  ))}
                </>
              )}
            </div>
          );
        })}
        {rows.length === 0 && !isLoading && (
          <div className="px-6 py-8 text-center text-sm text-text-muted">
            No appointments scheduled for this day
          </div>
        )}
      </div>
      )}
     </div>
     </div>
    </div>
  );
}

// ─── Agenda (Experiment, Antonin 13 Oct) ─────────────────────────────────
//
// "i don't understand the dayview in calendar … why is the booking shown in a looooong card". The day list was a
// table dressed as page-wide cards, with every working hour drawn even when empty. As an agenda it is the one list
// table: an hour line only where something is booked (plus the current hour, as the now line), then one 48 px row per
// booking on white, grey-100 dividers, grey hover, the same columns as the header above. The visit type's colour is a
// slim bar at the row's start; past rows are muted. Nothing else changes: the same cells, the same row actions.

interface AgendaBodyProps {
  bodyRef: React.RefObject<HTMLDivElement | null>;
  hours: number[];
  rows: DayListRow[];
  byHour: Map<number, DayListRow[]>;
  ghostsByHour: Map<number, SchedulingGhostRow[]>;
  currentHour?: number;
  isLoading: boolean;
  selectedDateISO?: string;
  onAppointmentClick?: (id: string) => void;
  onAppointmentAction?: (row: DayListRow, action: AppointmentActionId) => void;
}

function AgendaBody({ bodyRef, hours, rows, byHour, ghostsByHour, currentHour, isLoading, selectedDateISO, onAppointmentClick, onAppointmentAction }: AgendaBodyProps) {
  if (isLoading && rows.length === 0) {
    return (
      <div ref={bodyRef} className="pb-4" aria-busy="true">
        {[0, 1, 2].map((g) => (
          <div key={g} aria-hidden data-skeleton-row>
            <div className="flex h-10 items-center">
              <div className={cn(GUTTER, 'flex items-center justify-center')}><SkeletonBar className="h-4 w-10" /></div>
            </div>
            {[0, 1].map((r) => (
              <div key={r} className="flex">
                <div className={GUTTER} />
                <div className={cn(DATA_GRID, 'flex-1 min-h-12 border-b border-[var(--color-grey-100)] px-3 py-1.5')}>
                  <div data-col="time" className="flex flex-col gap-1"><SkeletonBar className="h-4 w-24" /><SkeletonBar className="h-3 w-14" /></div>
                  <div data-col="patient" className="flex items-center gap-2"><SkeletonBar className="h-8 w-8 shrink-0 rounded-full" /><SkeletonBar className="h-4 w-28" /></div>
                  <div data-col="dob" className="flex flex-col gap-1"><SkeletonBar className="h-4 w-20" /><SkeletonBar className="h-3 w-8" /></div>
                  <div data-col="doctor" className="flex flex-col gap-1"><SkeletonBar className="h-4 w-28" /><SkeletonBar className="h-3 w-20" /></div>
                  <div data-col="profile" className="flex flex-col gap-1"><SkeletonBar className="h-4 w-24" /><SkeletonBar className="h-3 w-12" /></div>
                  <div data-col="status"><SkeletonBar className="h-4 w-16" /></div>
                  <div data-col="note" />
                  <div data-col="actions" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Every hour of the day's range, empty ones included (Antonin, 15 Oct: "in the day view, also add the empty hours, not
  // like an agenda view"; was only the hours holding something, plus the current hour).
  const shownHours = [...hours];
  // Bookings outside the working-hours gutter still show (their own hour line).
  for (const h of byHour.keys()) if (!shownHours.includes(h)) shownHours.push(h);
  shownHours.sort((a, b) => a - b);

  return (
    <div ref={bodyRef} className="pb-4">
      {shownHours.map((hour) => {
        const apts = byHour.get(hour) ?? [];
        const ghosts = ghostsByHour.get(hour) ?? [];
        const isCurrent = hour === currentHour;
        return (
          <section key={hour} data-hour={hour} style={{ scrollMarginTop: 64 }} aria-label={formatHourLabel(hour)} className="[&>div:last-child>[data-agenda-row]]:border-b-0">
            {/* The hour line: the hour on the gutter, a hairline across (blue on the current hour, the now line). */}
            <div className="flex h-10 items-center">
              <div className={cn(GUTTER, 'flex items-center justify-center')}>
                <HourMarker hour={hour} active={isCurrent} />
              </div>
              <div className={cn('h-px flex-1', isCurrent ? 'bg-brand-primary' : 'bg-border-default')} />
            </div>
            {apts.map((apt) => (
              <AgendaRow key={apt.id} row={apt} selectedDateISO={selectedDateISO} onClick={onAppointmentClick} onAction={onAppointmentAction} />
            ))}
            {ghosts.map((ghost, idx) => (
              <GhostDraftRow key={ghost.id} ghost={ghost} hour={hour} isFirstInHour={false} isCurrentHour={isCurrent} motionIndex={idx} />
            ))}
            {apts.length === 0 && ghosts.length === 0 && (
              // An empty hour keeps one row's height (48 px, a booking's), so the day reads at a steady rhythm; the current
              // hour says so in words.
              <div className="flex min-h-12" data-empty-hour>
                <div className={GUTTER} />
                {isCurrent ? (
                  <p className="px-3 py-2 text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-muted">Nothing booked this hour</p>
                ) : null}
              </div>
            )}
          </section>
        );
      })}
      {rows.length === 0 && (
        <div className="px-6 py-8 text-center text-sm text-text-muted">
          No appointments scheduled for this day
        </div>
      )}
    </div>
  );
}

function formatHourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function AgendaRow({
  row,
  selectedDateISO,
  onClick,
  onAction,
}: {
  row: DayListRow;
  selectedDateISO?: string;
  onClick?: (id: string) => void;
  onAction?: (row: DayListRow, action: AppointmentActionId) => void;
}) {
  if (row.isBlock) {
    const label = row.note?.trim() || 'Blocked';
    return (
      <div className="flex">
        <div className={GUTTER} />
        <div
          onClick={() => onClick?.(row.id)}
          className={cn('my-1 flex min-h-10 flex-1 cursor-pointer items-center gap-2 rounded-md border border-border-default px-3', BLOCK_STRIPES)}
        >
          {row.isRecurring ? (
            <Repeat className="h-4 w-4 shrink-0 text-text-muted" aria-label="Recurring block" />
          ) : (
            <Ban className="h-4 w-4 shrink-0 text-text-muted" />
          )}
          <span className="shrink-0 text-sm font-semibold text-text-secondary">Blocked</span>
          {label !== 'Blocked' && <span className="truncate text-sm text-text-primary">— {label}</span>}
          <span className="ml-auto whitespace-nowrap text-sm tabular-nums text-text-muted">
            {row.timeRange}
            {row.room ? ` · ${row.room}` : ''}
          </span>
        </div>
      </div>
    );
  }
  const isPast = new Date(row.endISO).getTime() < Date.now();
  return (
    <div className="flex mt-2">
      <div className={GUTTER} />
      <div
        onClick={() => onClick?.(row.id)}
        className={cn(
          DATA_GRID,
          'relative flex-1 min-h-12 cursor-pointer rounded-lg pl-4 pr-3 py-1.5 transition-colors',
          isPast ? 'bg-[var(--color-grey-100)] [&_.text-text-primary]:text-text-muted [&_.text-text-secondary]:text-text-muted [&_img]:grayscale' : 'hover:shadow-sm',
        )}
        style={!isPast ? { backgroundColor: locationTileBg(row.profileLocation) } : undefined}
        data-past={isPast ? '' : undefined}
        data-agenda-row
        data-cal-row
      >
        <span
          aria-hidden
          data-event-edge
          className="absolute inset-y-2 left-1 w-[3px] rounded-full"
          style={{ background: isPast ? 'var(--color-text-muted)' : locationEdgeColor(row.profileLocation, row.profileColor) }}
        />
        <TimeCell row={row} />
        <PatientCell row={row} />
        <DobCell row={row} />
        <DoctorCell row={row} />
        <ProfileCell row={row} />
        <StatusCell row={row} />
        <NoteCell row={row} />
        <ActionsCell row={row} selectedDateISO={selectedDateISO} onAction={onAction} />
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

/**
 * GhostDraftRow — Scheduling-AI draft occurrence (VIEW-ONLY).
 *
 * Dashed border + "Draft" tag; amber (status-warning) variant when the
 * backend rejected the day (PROD-1846 alternatives verdict). Deliberately
 * NO click/hover affordances — a ghost is a sketch of intent, not an
 * appointment; the create rail's own flow books, and closing/discarding
 * the rail clears every ghost.
 */
function GhostDraftRow({
  ghost,
  hour,
  isFirstInHour,
  isCurrentHour,
  motionIndex = 0,
}: {
  ghost: SchedulingGhostRow;
  hour: number;
  isFirstInHour: boolean;
  isCurrentHour: boolean;
  /** Cascade position — staggers arrival to match the rail. */
  motionIndex?: number;
}) {
  return (
    <div
      className={cn('flex items-stretch', ghostMotion.ghostRow)}
      style={{ '--ghost-i': motionIndex } as CSSProperties}
      data-testid="scheduling-ai-ghost-row"
    >
      <div className={cn(GUTTER, 'flex items-center justify-center py-2')}>
        {isFirstInHour && <HourMarker hour={hour} active={isCurrentHour} />}
      </div>
      <div
        aria-label={`Draft appointment (not booked): ${ghost.label}, ${ghost.timeRange}`}
        className={cn(
          'flex-1 my-1 px-3 py-2.5 rounded-lg border-2 border-dashed flex items-center gap-2 select-none',
          ghost.rejected
            ? 'border-status-warning bg-status-warning/5'
            : 'border-brand-primary/40 bg-brand-primary/5',
        )}
      >
        <span
          className={cn(
            'px-2 py-0.5 rounded text-[length:var(--type-meta-size)] font-medium leading-none text-white flex-shrink-0',
            ghost.rejected ? 'bg-status-warning' : 'bg-brand-primary',
          )}
        >
          Draft
        </span>
        <span className="text-sm font-medium text-text-primary truncate">
          {ghost.label}
        </span>
        {ghost.sublabel ? (
          <span className="text-xs text-text-muted truncate">{ghost.sublabel}</span>
        ) : null}
        {ghost.rejected ? (
          <span className="text-xs font-medium text-[var(--color-status-warning-text)] whitespace-nowrap flex-shrink-0">
            day unavailable — see suggestions
          </span>
        ) : null}
        <span className="ml-auto text-sm text-text-muted whitespace-nowrap tabular-nums flex-shrink-0">
          {ghost.timeRange}
        </span>
      </div>
    </div>
  );
}

function HourMarker({ hour, active }: { hour: number; active?: boolean }) {
  const display = formatHourMark(hour, useTimeFormat());
  return (
    // A pill that hugs the time, not a fixed 32 px disc (Antonin, 13 Oct: "this round stuff is dirty"; "18:00" no
    // longer fit in it). The hour on the label role in meta ink, like the week gutter; the current hour on a soft blue
    // pill with white text (Antonin: "white text on full blue"), the now line's colour.
    <div
      data-hour-marker={active ? 'current' : undefined}
      className={cn(
        'inline-flex h-6 items-center rounded-md px-2 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] tabular-nums transition-colors',
        active
          ? 'bg-brand-primary text-white'
          : 'text-[var(--color-text-subtle)]',
      )}
    >
      {display}
    </div>
  );
}

/** A booking-sized grey card (same box, grid and line heights as AppointmentCardRow) while the day loads. */
function SkeletonCardRow({ hour, isCurrent }: { hour: number; isCurrent: boolean }) {
  const lines = (col: string, a: string, b: string) => (
    <div data-col={col} className="flex min-w-0 flex-col gap-1 py-0.5">
      <SkeletonBar className={cn('h-4', a)} />
      <SkeletonBar className={cn('h-3', b)} />
    </div>
  );
  return (
    <div className="flex items-stretch" aria-hidden data-skeleton-row>
      <div className={cn(GUTTER, 'flex items-center justify-center py-2')}>
        <HourMarker hour={hour} active={isCurrent} />
      </div>
      <div className={cn(DATA_GRID, 'relative flex-1 my-1 pl-4 pr-3 py-2 rounded-lg border border-border-subtle bg-surface-card')}>
        <span className="absolute inset-y-0 left-0 w-1 rounded-l-[8px] bg-grey-200" />
        {lines('time', 'w-24', 'w-16')}
        <div data-col="patient" className="flex min-w-0 items-center gap-2">
          <SkeletonBar className="h-8 w-8 shrink-0 rounded-full" />
          <SkeletonBar className="h-4 w-28" />
        </div>
        {lines('dob', 'w-20', 'w-8')}
        {lines('doctor', 'w-28', 'w-20')}
        {lines('profile', 'w-24', 'w-12')}
        <div data-col="status"><SkeletonBar className="h-4 w-16" /></div>
        <div data-col="note" />
        <div data-col="actions" />
      </div>
    </div>
  );
}

function EmptyHourRow({
  hour,
  isCurrent,
}: {
  hour: number;
  isCurrent: boolean;
}) {
  return (
    <div className="flex">
      <div className={cn(GUTTER, 'flex items-center justify-center py-3')}>
        <HourMarker hour={hour} active={isCurrent} />
      </div>
      <div className="flex-1 flex flex-col justify-center gap-3 py-3">
        <div className="border-b border-border-subtle" />
        <div className="border-b border-border-subtle" />
      </div>
    </div>
  );
}

function AppointmentCardRow({
  row,
  hour,
  isFirstInHour,
  isCurrentHour,
  selectedDateISO,
  onClick,
  onAction,
}: {
  row: DayListRow;
  hour: number;
  isFirstInHour: boolean;
  isCurrentHour: boolean;
  selectedDateISO?: string;
  onClick?: (id: string) => void;
  onAction?: (row: DayListRow, action: AppointmentActionId) => void;
}) {
  // Block Availability entry — a distinct striped row (block reason + time),
  // not the patient grid (which would render an "Unknown" patient). Flutter
  // parity: the block card in `calendar_event_card_widget.dart`.
  if (row.isBlock) {
    const label = row.note?.trim() || 'Blocked';
    return (
      <div className="flex items-stretch">
        <div className={cn(GUTTER, 'flex items-center justify-center py-2')}>
          {isFirstInHour && <HourMarker hour={hour} active={isCurrentHour} />}
        </div>
        <div
          onClick={() => onClick?.(row.id)}
          className={cn(
            'flex-1 my-1 px-3 py-2.5 rounded-lg shadow-sm border border-border-default cursor-pointer hover:shadow-md transition-shadow flex items-center gap-2',
            BLOCK_STRIPES,
          )}
        >
          {row.isRecurring ? (
            <Repeat className="h-4 w-4 text-text-muted shrink-0" aria-label="Recurring block" />
          ) : (
            <Ban className="h-4 w-4 text-text-muted shrink-0" />
          )}
          <span className="text-sm font-semibold text-text-secondary shrink-0">
            Blocked
          </span>
          {label !== 'Blocked' && (
            <span className="text-sm text-text-primary truncate">— {label}</span>
          )}
          <span className="ml-auto text-sm text-text-muted whitespace-nowrap">
            {row.timeRange}
            {row.room ? ` · ${row.room}` : ''}
          </span>
        </div>
      </div>
    );
  }

  const isPast = new Date(row.endISO).getTime() < Date.now();
  return (
    <div className="flex items-stretch">
      {/* Gutter — only first row of an hour shows the marker */}
      <div className={cn(GUTTER, 'flex items-center justify-center py-2')}>
        {isFirstInHour && <HourMarker hour={hour} active={isCurrentHour} />}
      </div>

      {/* Card — my-2 for breathing room between consecutive cards in the same slot */}
      <div
        onClick={() => onClick?.(row.id)}
        className={cn(
          DATA_GRID,
          'relative flex-1 my-2 pl-4 pr-3 py-2 rounded-lg shadow-sm border border-border-subtle cursor-pointer hover:shadow-md transition-shadow',
          isPast ? 'bg-[var(--color-grey-100)] [&_.text-text-primary]:text-text-muted [&_.text-text-secondary]:text-text-muted [&_img]:grayscale' : '',
        )}
        style={!isPast ? { backgroundColor: locationTileBg(row.profileLocation) } : undefined}
        data-past={isPast ? '' : undefined}
        data-cal-row
      >
        <span
          aria-hidden
          data-event-edge
          className="absolute inset-y-0 left-0 w-1 rounded-l-[8px]"
          style={{ background: isPast ? 'var(--color-text-muted)' : locationEdgeColor(row.profileLocation, row.profileColor) }}
        />
        <TimeCell row={row} />
        <PatientCell row={row} />
        <DobCell row={row} />
        <DoctorCell row={row} />
        <ProfileCell row={row} />
        <StatusCell row={row} />
        <NoteCell row={row} />
        <ActionsCell
          row={row}
          selectedDateISO={selectedDateISO}
          onAction={onAction}
        />
      </div>
    </div>
  );
}

// ─── Cells ───────────────────────────────────────────────────────────────

function TimeCell({ row }: { row: DayListRow }) {
  return (
    <div className="flex flex-col items-start gap-0.5 min-w-0" data-col="time" data-time-stack>
      {/* Leading icon strip — on a line ABOVE the time (Antonin's export, 23 Oct): beside it, the narrower Time column
          (108 min) cut the longest ranges ("10:00a - 10:20a") by a few pixels, measured live 23 Oct.
          Order (Andrew 2026-06-10): location → recurring (if any) → flag
          (if any) → note. Colours:
          - recurring: shown for series members. Flutter tints it
            `progressColor` (#FCC001 amber), but amber is a Signal-rejected
            decoration colour, so we use brand-primary — consistent with the
            week/day grid `AppointmentTile` recurring glyph.
          - flag: Flutter uses `enabledBtnBGColor` (#00B8E3 legacy teal →
            Signal Blue/500 migration anchor) = brand-primary. NOT
            status-warning orange (that's a clinical-caution token; using it
            here was a rule-2 decoration misuse). */}
      <div className="flex items-center gap-1 text-text-muted flex-shrink-0">
        <LeadingLocationIcon
          location={row.profileLocation}
          color={row.profileColor}
        />
        {/* Neutral, like the location icon beside them (Antonin, 15 Oct: "the icon colours on day calendar items are bad,
            put all in neutral"). */}
        {row.isRecurring && <ScripsIcon name="recurring" size={12} />}
        {row.hasFlag && <ScripsIcon name="flag" size={12} />}
        {row.hasNote && <ScripsIcon name="comment" size={12} />}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-text-primary tabular-nums whitespace-nowrap">
          {row.timeRange}
        </div>
        {row.room && (
          <div className="text-xs text-text-muted truncate" title={row.room}>
            {row.room}
          </div>
        )}
      </div>
    </div>
  );
}

function PatientCell({ row }: { row: DayListRow }) {
  return (
    <div className="flex items-center gap-3 min-w-0" data-col="patient">
      <PatientAvatar
        name={row.patientName}
        photoUrl={row.patientAvatar}
        importance={row.patientImportance}
        size="md"
      />
      <div className="min-w-0 flex items-center gap-2">
        <span
          className="text-sm font-medium text-text-primary truncate"
          title={row.patientName}
        >
          {row.patientName}
        </span>
        {row.patientBadge && (
          <span className="px-2 py-0.5 rounded bg-brand-primary text-white text-xs font-bold leading-none flex-shrink-0">
            {row.patientBadge}
          </span>
        )}
      </div>
    </div>
  );
}

function DobCell({ row }: { row: DayListRow }) {
  if (!row.dob) return <div data-col="dob" />;
  return (
    <div className="min-w-0" data-col="dob">
      <div className="text-sm text-text-primary truncate">{row.dob}</div>
      {row.age && (
        <div className="text-xs text-text-muted">{row.age}</div>
      )}
    </div>
  );
}

function DoctorCell({ row }: { row: DayListRow }) {
  if (!row.doctorName) return <div data-col="doctor" />;
  return (
    <div className="flex items-center gap-2 min-w-0" data-col="doctor">
      <PatientAvatar
        name={row.doctorName}
        photoUrl={row.doctorPhoto}
        variant="provider"
        size={24}
      />
      <div className="min-w-0">
        <div
          className="text-sm font-medium text-text-primary truncate"
          title={row.doctorName}
        >
          {row.doctorName}
        </div>
        {row.doctorSpecialty && (
          <div className="text-xs text-text-muted truncate" title={row.doctorSpecialty}>
            {row.doctorSpecialty}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileCell({ row }: { row: DayListRow }) {
  if (!row.profileName && !row.reasonForVisit) return <div data-col="profile" />;
  return (
    <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" data-col="profile">
      {row.profileName && (
        <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-text-primary truncate" title={row.profileName}>
          {row.profileName}
        </div>
      )}
      {row.reasonForVisit && (
        <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-text-muted truncate" title={row.reasonForVisit}>
          {row.reasonForVisit}
        </div>
      )}
    </div>
  );
}

function StatusCell({ row }: { row: DayListRow }) {
  return (
    <div className="flex items-center" data-col="status">
      {/* Canonical StatusBadge atom (unified across the app — day list, tiles,
          by-doctor/by-room, patient appointments). `label` carries the API
          display so exact wording shows; the atom forces "Completed" for
          fulfilled. */}
      <StatusBadge status={row.status} label={row.statusDisplay} />
    </div>
  );
}

/** Row action icons shown inline before the ⋯ (the 140 actions column holds four 32 icons 4 apart). */
const MAX_INLINE_ROW_ACTIONS = 3;

function NoteCell({ row }: { row: DayListRow }) {
  const [open, setOpen] = useState(false);
  if (!row.note) return <div data-col="note" />;
  // Two lines, then an ellipsis; the whole note opens in a card on hover (Antonin, 23 Oct). A portal card, so the
  // row never clips it; it stays open while the pointer is over it, so a long note can scroll.
  return (
    // Focusable, and the card opens on focus too: with no native title, a keyboard user reaches the whole note this way.
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        openOnHover
        delay={0}
        closeDelay={0}
        nativeButton={false}
        render={
          <div
            data-col="note"
            data-note-clamp
            tabIndex={0}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            className="day-note-clamp min-w-0 text-sm text-text-muted"
          />
        }
      >
        {row.note}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        data-slot="note-card"
        className="w-[380px] max-w-[calc(100vw-24px)] gap-0.5 rounded-[var(--radius-control)] border border-[var(--surface-line)] bg-[var(--surface-read)] px-4 pb-4 pt-3.5 shadow-none ring-0"
      >
        <span className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-[var(--ink-secondary)]">
          Appointment note
        </span>
        <div className="max-h-60 overflow-auto whitespace-pre-wrap text-[length:var(--type-body-size)] leading-[1.5] text-[var(--ink-body)]">
          {row.note}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ActionsCell({
  row,
  selectedDateISO,
  onAction,
}: {
  row: DayListRow;
  selectedDateISO?: string;
  onAction?: (row: DayListRow, action: AppointmentActionId) => void;
}) {
  const updateStatus = useUpdateAppointmentStatus();
  // Mark-Arrived incomplete-patient-data guard (PROD-989).
  const [arrivedAlertKind, setArrivedAlertKind] = useState<ArrivedGuardKind>(null);

  const isToday = selectedDateISO
    ? isSameDayISO(row.startISO, selectedDateISO)
    : isSameDayISO(row.startISO, new Date().toISOString());

  const actions = getRowActionsFor({
    statusCode: row.statusCode,
    isToday,
    isHomeLocation: (row.locationName ?? '').toLowerCase() === 'home',
    isRecurring: row.isRecurring,
  });

  if (actions.length === 0) {
    return <div data-col="actions" />;
  }

  const handle = (action: AppointmentAction) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const newCode = statusCodeForAction(action.id);
    if (newCode) {
      // Block Mark-Arrived when patient identity/guardian data is incomplete
      // (Flutter `showIncompletePatientDataAlert`). Same guard as the popup /
      // panel status manager — shared `arrivedGuardStatus`.
      if (action.id === 'mark-arrived') {
        const { blocked, kind } = arrivedGuardStatus({
          identificationId: row.patientIdentificationId,
          guardianIdentificationId: row.guardianIdentificationId,
          guardianRelationId: row.guardianRelationId,
          birthDateISO: row.patientBirthDateISO,
          isMissingData: row.patientIsMissingData,
        });
        if (blocked) {
          setArrivedAlertKind(kind);
          return;
        }
      }
      updateStatus.mutate({
        appointmentId: row.id,
        status: { code: newCode, display: newCode, definition: '' },
      });
      return;
    }
    onAction?.(row, action.id);
  };

  // At most three icons inline, then the ⋯ (Antonin's export, 22-23 Oct: four 32 icons fill the 140 column). A
  // further action moves into the ⋯ menu; Cancel is there already, so it is not listed twice.
  const inline = actions.filter((a) => a.id !== 'overflow').slice(0, MAX_INLINE_ROW_ACTIONS);
  const moved = actions.filter((a) => a.id !== 'overflow').slice(MAX_INLINE_ROW_ACTIONS).filter((a) => a.id !== 'cancel');
  const hasOverflow = actions.some((a) => a.id === 'overflow') || moved.length > 0;

  return (
    <div className="flex items-center justify-end gap-[var(--spacing-1)]" data-col="actions">
      {inline.map((action) => (
        <ActionButton
          key={action.id}
          id={action.id}
          icon={action.icon}
          label={action.label}
          tone={action.tone}
          onClick={handle(action)}
          disabled={updateStatus.isPending}
        />
      ))}
      {hasOverflow ? (
        <OverflowMenu row={row} onAction={onAction} moved={moved.map((a) => ({ action: a, onClick: handle(a) }))} />
      ) : null}

      {/* Incomplete-patient-data guard (PROD-989). "Proceed to complete
          patient" routes to the edit flow (Flutter parity). */}
      <AlertDialog
        open={arrivedAlertKind !== null}
        onOpenChange={(next) => {
          if (!next) setArrivedAlertKind(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
          <AlertDialogHeader>
            <AlertDialogTitle>{ARRIVED_GUARD_TITLE}</AlertDialogTitle>
            <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
              {arrivedGuardMessage(arrivedAlertKind)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setArrivedAlertKind(null);
                onAction?.(row, 'edit');
              }}
            >
              {ARRIVED_GUARD_PROCEED}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Overflow ⋯ menu — status-aware. Mirrors Flutter `menuOptions(...)`
 * (calendar_tab.dart:2566-2748). Ships the items wired in this milestone:
 *   - Add / Edit note (always)
 *   - Edit Appointment (PROD-914 — always)
 *   - Open 'Billing' Tab (PROD-915 — Show_Billing_Tab gate + status NOT
 *     in {Booked, Pending, NoShow}; predicate in `appointment-actions.ts`)
 *   - View Recurring Series (PROD-916 — only when `row.isRecurring`)
 *   - Cancel appointment (hidden when terminal)
 */
function OverflowMenu({
  row,
  onAction,
  moved = [],
}: {
  row: DayListRow;
  onAction?: (row: DayListRow, action: AppointmentActionId) => void;
  /** Row actions past the three inline icons, listed first in the menu with their own handlers. */
  moved?: { action: AppointmentAction; onClick: (e: React.MouseEvent) => void }[];
}) {
  const [open, setOpen] = useState(false);
  const code = (row.statusCode ?? '').toLowerCase();
  const isTerminal =
    code === 'cancelled' ||
    code === 'canceled' ||
    code === 'noshow' ||
    code === 'no-show' ||
    code === 'fulfilled';

  const canViewBillingTab = usePermission(['Show_Billing_Tab'], { view: true });
  const showBillingTabItem =
    canViewBillingTab && shouldShowBillingTabMenuItem(row.statusCode);
  const showRecurringSeriesItem = shouldShowViewRecurringSeriesMenuItem(row.isRecurring);
  const showPvsItem = shouldShowPvsMenuItem(row.statusCode);

  const handle = (action: AppointmentActionId) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onAction?.(row, action);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="More actions"
            onClick={(e) => e.stopPropagation()}
            className={cn(ROW_ICON_BUTTON, 'text-text-secondary')}
          />
        }
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-56 p-1 gap-0"
      >
        {moved.map(({ action, onClick }) => (
          <MenuItem
            key={action.id}
            icon={action.icon}
            label={action.label}
            tone={action.tone === 'error' ? 'error' : 'default'}
            onClick={(e) => {
              setOpen(false);
              onClick(e);
            }}
          />
        ))}
        <MenuItem
          icon="comment"
          label={row.note ? 'Edit note' : 'Add note'}
          onClick={handle('add-note')}
        />
        <MenuItem
          icon="edit"
          label="Edit Appointment"
          onClick={handle('edit')}
        />
        {showPvsItem && (
          <MenuItem
            icon="preview"
            label="View PVS"
            onClick={handle('view-pvs')}
          />
        )}
        {showBillingTabItem && (
          <MenuItem
            icon="billing"
            label="Open 'Billing' Tab"
            onClick={handle('open-billing-tab')}
          />
        )}
        {showRecurringSeriesItem && (
          <MenuItem
            icon="recurring"
            label="View Recurring Series"
            onClick={handle('view-recurring-series')}
          />
        )}
        {!isTerminal && (
          <MenuItem
            icon="cancelled"
            label="Cancel appointment"
            tone="error"
            onClick={handle('cancel')}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({
  icon,
  label,
  tone = 'default',
  onClick,
}: {
  icon: ScripsIconName;
  label: string;
  tone?: 'default' | 'error';
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded text-left transition-colors',
        tone === 'error'
          ? 'text-status-error hover:bg-status-error/10'
          : 'text-text-primary hover:bg-grey-100',
      )}
    >
      <ScripsIcon name={icon} size={14} className="flex-shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * The ⋯ trigger glyph isn't in ScripsIcons, so render the standard
 * three-dots SVG inline. PopoverTrigger needs a single child for its
 * `render` slot.
 */

/** Row action icons: one Lucide line set at its standard 2 px stroke, chosen per action (Antonin, 13 Oct: "icons are
 *  inconsistent. Have the right icon with the right thickness"). The Scrips font glyphs stay a fallback. */
const ROW_ACTION_ICON: Partial<Record<AppointmentActionId, LucideIcon>> = {
  'mark-arrived': UserCheck,
  confirm: Check,
  'mark-checked-in': LogIn,
  'mark-no-show': UserX,
  'unmark-arrived': Undo2,
  'unmark-checked-in': Undo2,
  'unmark-no-show': Undo2,
  reschedule: CalendarClock,
  cancel: CalendarX2,
  edit: Pencil,
  'follow-up': CalendarPlus,
};

function ActionButton({
  id,
  icon,
  label,
  tone,
  onClick,
  disabled,
}: {
  id?: AppointmentActionId;
  icon: ScripsIconName;
  label: string;
  tone: 'success' | 'warning' | 'info' | 'error' | 'muted';
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
}) {
  // Neutral button, neutral icon (Antonin, 13 Oct: "have them bigger and in a button. have the tooltip on hover"; 15 Oct:
  // "the icon colours on day calendar items are bad, put all in neutral"). The tooltip names the action; `tone` stays in
  // the data for callers but no longer colours the icon.
  void tone;
  const toneClass = 'text-text-secondary';
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
            className={cn(ROW_ICON_BUTTON, toneClass)}
          />
        }
      >
        {(() => {
          const BridgeIcon = id ? ROW_ACTION_ICON[id] : undefined;
          return BridgeIcon ? <BridgeIcon className="h-4 w-4" aria-hidden /> : <ScripsIcon name={icon} size={16} />;
        })()}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function isSameDayISO(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function LeadingLocationIcon({
  location,
  color,
}: {
  location?: string;
  color?: string;
}) {
  // Use a real ternary so an empty-string `location` falls through to
  // 'office' — `location && LOCATION_ICONS[...]` would return ""
  // (falsy but typed as string), making the return type "" | ScripsIconName.
  const iconName: ScripsIconName = !location
    ? 'office'
    : LOCATION_ICONS[location.toLowerCase()] ?? 'office';
  return (
    // Neutral (15 Oct): the visit type's colour already shows as the row's slim leading bar.
    <ScripsIcon name={iconName} size={13} data-profile-color={color || undefined} />
  );
}

// Status pill — now the canonical `StatusBadge` atom (see StatusCell above).
// The former local `STATUS_BADGE` map + `StatusPill` were removed in the
// system-wide status-colour unification; the atom is the single source.
